// Public marketing / explainer page for Prime Agent Direct Connect. Linked from the
// onboarding prompt and the Settings "Prime Agent Direct Connect" section for users
// who don't yet have Prime Agent. Pricing copy is placeholder pending finalized plans.

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Lock as LockIcon,
  Bolt as BoltIcon,
} from "@mui/icons-material";

const FEATURES = [
  {
    icon: <BankIcon color="primary" />,
    title: "Banks Plaid can't reach",
    body: "Prime Agent signs into “Direct Connect” banks that aggregators don't support and pulls your transactions automatically.",
  },
  {
    icon: <LockIcon color="primary" />,
    title: "Credentials stay encrypted",
    body: "Your bank login is encrypted at rest and only decrypted for the moment Prime Agent signs in on your behalf.",
  },
  {
    icon: <BoltIcon color="primary" />,
    title: "On-demand, no waiting",
    body: "Focus asks Prime Agent to sync only when you need it — hit Sync and your latest transactions import in one pass.",
  },
];

const PrimeAgentSalesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 6 }}>
        <BankIcon color="primary" sx={{ fontSize: 56 }} />
        <Typography variant="h3" fontWeight={700}>
          Meet Prime Agent
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 620 }}>
          Automatic transaction import for the banks Plaid can't connect to.
          Prime Agent logs in for you, downloads your statements, and drops the
          transactions straight into Focus: Finance.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/settings")}
          >
            Connect Prime Agent
          </Button>
          <Button size="large" onClick={() => navigate("/dashboard")}>
            Back to app
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2} sx={{ mb: 6 }}>
        {FEATURES.map((f) => (
          <Card key={f.title} variant="outlined">
            <CardContent
              sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
            >
              <Box sx={{ mt: 0.5 }}>{f.icon}</Box>
              <Box>
                <Typography variant="h6">{f.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {f.body}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Card sx={{ mb: 6 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Placeholder — final plans TBD.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {[
              "Connect unlimited Direct-Connect banks",
              "On-demand and scheduled syncs",
              "Two-factor codes relayed securely through Focus",
            ].map((item) => (
              <ListItem key={item} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate("/settings")}
          >
            Get started
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PrimeAgentSalesPage;
